import { ReactNode, RefCallback, useRef, useState } from "react";
import { CloseIcon, EditIcon } from "@chakra-ui/icons";
import {
  Box,
  Alert,
  AlertDescription,
  Button,
  InputGroup,
  InputRightAddon,
  Text,
} from "@chakra-ui/react";
import ConfirmModalIconButton from "components/Shared/ConfirmModal/ConfirmModalIconButton";
import TooltipIconButton from "components/Shared/TooltipIconButton";

type Props = {
  isValuePresent: boolean;
  renderInput: (props: {
    inputRef: RefCallback<HTMLInputElement>;
    isReadOnly: boolean;
  }) => ReactNode;
};

export default function ReadOnlyIdWrapper({
  isValuePresent,
  renderInput,
}: Props) {
  const [isEdit, setIsEdit] = useState(false);

  const inputRefObj = useRef<HTMLInputElement | null>();
  const inputRef = (c: HTMLInputElement | null) => {
    inputRefObj.current = c;
  };

  if (!isValuePresent) {
    return renderInput({ inputRef, isReadOnly: false });
  }

  return (
    <InputGroup
      sx={{
        input: {
          borderRightRadius: 0,
          "&[readonly]": { backgroundColor: "gray.50" },
        },
      }}
    >
      {renderInput({
        inputRef,
        isReadOnly: isValuePresent && !isEdit,
      })}

      <InputRightAddon
        p={0}
        alignItems="normal"
        sx={{ button: { px: 3, borderLeftRadius: 0, minW: "2.6rem" } }}
      >
        {isEdit && (
          <TooltipIconButton
            icon={<CloseIcon boxSize=".75em" />}
            label="Cancel Editing"
            onClick={() => setIsEdit(false)}
            variant="ghost"
            color="gray.600"
            tabIndex={-1}
          />
        )}

        <ConfirmModalIconButton
          label="Edit Existing Identifier…"
          icon={<EditIcon boxSize=".85em" />}
          display={isEdit ? "none" : undefined}
          confirmText={
            <Box sx={{ "p + p": { mt: 3 } }}>
              <Text>
                SKU identifiers should not generally be edited after they have
                been set unless they were initially entered incorrectly.
              </Text>
              <Text>
                If this product has a new UPC, you should instead create a new
                SKU and archive this SKU.
              </Text>
              <Alert status="warning" my={3}>
                <AlertDescription>
                  <Text>
                    SKU identifiers are used to identify and link SKUs
                    throughout the app and changing them may have wide-ranging
                    unintended consequences.
                  </Text>
                </AlertDescription>
              </Alert>
              <Text fontWeight={500}>
                Are you sure you want to edit this identifier?
              </Text>
            </Box>
          }
          renderConfirmButton={({ onClose: onCloseConfirm }) => (
            <Button
              colorScheme="pinkButton"
              size="sm"
              onClick={() => {
                setIsEdit(true);
                onCloseConfirm();
                const inputEl = inputRefObj.current;
                if (inputEl) {
                  setTimeout(() => {
                    inputEl.focus();
                  }, 500);
                }
              }}
            >
              Edit Existing Identifier
            </Button>
          )}
          variant="ghost"
          color="gray.600"
          tabIndex={-1}
        />
      </InputRightAddon>
    </InputGroup>
  );
}
